import React from "react";
import {
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
} from "recharts";
import { PieChart as PieChartIcon, Youtube } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCompactNumber } from "@/lib/utils/formatNumber";
import type { PlatformDistributionItem } from "../../types/analytics.types";

export type { PlatformDistributionItem };

export interface PlatformDistributionChartProps {
  data: PlatformDistributionItem[];
  title?: string;
  subtitle?: string;
  totalViews?: number | string;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}

export const PlatformDistributionChart: React.FC<PlatformDistributionChartProps> = ({
  data,
  title = "Platform Distribution",
  subtitle = "Breakdown of engagement by platform",
  totalViews = 0,
  isLoading = false,
  isError = false,
  onRetry,
}) => {
  const displayViews =
    typeof totalViews === "number"
      ? formatCompactNumber(totalViews)
      : totalViews || "0";

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3.5 mb-6">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <div className="space-y-1.5">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-56" />
          </div>
        </div>
        <Skeleton className="h-[220px] w-[220px] rounded-full mx-auto" />
        <div className="mt-4 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3.5 mb-6">
          <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center shrink-0">
            <PieChartIcon className="w-5 h-5 text-pink-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-[220px] text-gray-500">
          <p className="text-sm">Failed to load platform data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-2 text-sm text-purple-600 hover:text-purple-700 underline"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3.5 mb-6">
          <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center shrink-0">
            <PieChartIcon className="w-5 h-5 text-pink-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center h-[220px] text-gray-400">
          <p className="text-sm">No posts found for this campaign.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center gap-3.5 mb-6">
        <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center shrink-0">
          <PieChartIcon className="w-5 h-5 text-pink-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>

      {/* Donut Chart with Centered Total Views */}
      <div className="relative flex items-center justify-center">
        <ResponsiveContainer width="100%" height={220}>
          <RechartsPieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={68}
              outerRadius={88}
              paddingAngle={data.length > 1 ? 4 : 0}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
              ))}
            </Pie>
            <Tooltip
              formatter={(val: number) => [`${val}%`, 'Engagement Share']}
              contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                fontSize: '12px',
              }}
            />
          </RechartsPieChart>
        </ResponsiveContainer>

        {/* Center Total Views Label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none select-none">
          <span className="text-3xl font-bold text-slate-900 tracking-tight">
            {displayViews}
          </span>
          <span className="text-xs sm:text-sm font-medium text-slate-500 mt-0.5">
            Total Views
          </span>
        </div>
      </div>

      {/* Platform Breakdown Cards */}
      <div className="mt-4 space-y-3">
        {data.map((platform, index) => {
          const platformLower = platform.name.toLowerCase();
          const platformViews =
            typeof totalViews === "number"
              ? formatCompactNumber(
                Math.round((totalViews * platform.value) / 100)
              )
              : displayViews;

          return (
            <div
              key={index}
              className="flex items-center justify-between p-3.5 px-4 rounded-2xl bg-pink-50/60 border border-pink-100/60 transition-colors hover:bg-pink-50/80"
            >
              <div className="flex items-center gap-3.5">
                <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center shadow-xs border border-pink-100/50 shrink-0">
                  {platformLower === "youtube" ? (
                    <Youtube className="w-6 h-6 text-red-500" />
                  ) : platformLower === "instagram" ? (
                    <img
                      src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                      alt="Instagram"
                      className="w-8 h-8 object-contain"
                    />
                  ) : (
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: platform.color }}
                    />
                  )}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 leading-tight">
                    {platform.name}
                  </h4>
                  <p className="text-xs text-gray-500 font-normal mt-0.5">
                    {platformViews} views
                  </p>
                </div>
              </div>
              <span className="text-base font-bold text-gray-900">
                {platform.value}%
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformDistributionChart;
